import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  apiMessage,
  bookPublicSlot,
  fmtDay,
  fmtTime,
  getPublicSlotSheet,
  localZoneName,
  TBookResult,
  TPublicSlot,
} from '../../../requests/opsSlots';

/**
 * The booker's page: a signed link, no login. Pick a time, leave a name and
 * email, done. Nothing here names another booker; a wrong link and a closed
 * sheet each get a plain sentence rather than an error screen.
 */

const ZONE_LABEL: Record<string, string> = {
  PST: 'Pacific time',
  EST: 'Eastern time',
};

const input =
  'w-full rounded-lg border border-white-two bg-white px-3.5 py-2.5 text-base text-gray-four placeholder:text-gray-two focus:border-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-100';
const label = 'mb-1 block text-sm font-medium text-gray-five';
const primary =
  'inline-flex w-full items-center justify-center rounded-lg bg-blue-primary px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-blue-one disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto';
const ghost =
  'inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium text-blue-primary hover:bg-blue-100';

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-grey-50 px-4 py-8 sm:py-14">
    <div className="mx-auto w-full max-w-lg">
      <div className="mb-5 text-sm font-semibold tracking-wide text-gray-bold">
        Learn iT!
      </div>
      <div className="rounded-2xl border border-white-regular bg-white p-6 shadow-light sm:p-8">
        {children}
      </div>
    </div>
  </div>
);

const Notice: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <Shell>
    <h1 className="text-xl font-bold text-gray-four">{title}</h1>
    <p className="mt-2 text-base text-gray-three">{body}</p>
  </Shell>
);

const BookSlot: React.FC = () => {
  const { sheetId = '' } = useParams<{ sheetId: string }>();
  const [searchParams] = useSearchParams();
  const sig = searchParams.get('sig') ?? '';

  const [slotId, setSlotId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [done, setDone] = useState<TBookResult | null>(null);
  const [problem, setProblem] = useState('');

  const {
    data: sheet,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['publicSlotSheet', sheetId, sig],
    queryFn: () => getPublicSlotSheet(sheetId, sig),
    enabled: Boolean(sheetId && sig),
    retry: false,
  });

  const book = useMutation({
    mutationFn: () =>
      bookPublicSlot(sheetId, sig, {
        slotId,
        name: name.trim(),
        email: email.trim(),
        note: note.trim() || undefined,
      }),
    onSuccess: (r) => {
      setDone(r);
      setProblem('');
    },
    onError: (err) => {
      setProblem(apiMessage(err, 'That did not go through — try again'));
      // A slot that filled up while the page was open drops out of the list
      refetch();
    },
  });

  const days = useMemo(() => {
    const groups = new Map<string, TPublicSlot[]>();
    for (const s of sheet?.slots ?? []) {
      const key = fmtDay(s.start);
      groups.set(key, [...(groups.get(key) ?? []), s]);
    }
    return Array.from(groups.entries());
  }, [sheet]);

  if (!sheetId || !sig) {
    return (
      <Notice
        title="This link is missing its key"
        body="Open the link exactly as it was sent to you. If it still does not work, reply to the person who sent it."
      />
    );
  }
  if (isLoading) {
    return (
      <Shell>
        <div className="text-base text-gray-three">Loading…</div>
      </Shell>
    );
  }
  if (error || !sheet) {
    const status = axios.isAxiosError(error) ? error.response?.status : 0;
    return (
      <Notice
        title={
          status === 404 ? 'This link is not valid' : 'Something went wrong'
        }
        body={
          status === 404
            ? 'It may have been copied incompletely, or the sign-up sheet has been removed. Reply to the person who sent it and they can send a fresh one.'
            : 'Please try again in a moment.'
        }
      />
    );
  }
  if (sheet.closed) {
    return (
      <Notice
        title="This sign-up sheet is closed"
        body="No more times are being offered through this link. If you still need a time, reply to the person who sent it."
      />
    );
  }

  const zone = sheet.timeZone
    ? ZONE_LABEL[sheet.timeZone] ?? sheet.timeZone
    : null;
  const localZone = localZoneName();

  if (done) {
    return (
      <Shell>
        <div className="mb-1 text-sm font-medium text-green-900">
          {done.moved ? 'Your time has been moved' : 'You are booked'}
        </div>
        <h1 className="text-xl font-bold text-gray-four">{done.title}</h1>
        <div className="mt-4 rounded-xl bg-blue-100 px-4 py-3">
          <div className="text-base font-semibold text-gray-four">
            {fmtDay(done.start)}
          </div>
          <div className="text-base text-gray-five">
            {fmtTime(done.start)} – {fmtTime(done.end)}
            <span className="ml-2 text-sm text-gray-bold">({localZone})</span>
          </div>
        </div>
        <p className="mt-4 text-sm text-gray-three">
          Booked for <b className="text-gray-five">{done.name}</b> ·{' '}
          {done.email}
        </p>
        {done.registration && !done.registration.ok && (
          <p className="mt-2 text-sm text-gray-three">
            Your time is held. The class roster could not be updated
            automatically ({done.registration.message}); the team will sort that
            out.
          </p>
        )}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center">
          <a className={primary} href={done.icsLink}>
            Add to calendar
          </a>
          <button
            type="button"
            className={ghost}
            onClick={() => {
              setDone(null);
              setSlotId('');
              refetch();
            }}
          >
            Change my time
          </button>
        </div>
        <p className="mt-4 text-xs text-gray-bold">
          Booking again with the same email moves your time rather than adding a
          second one.
        </p>
      </Shell>
    );
  }

  const canSubmit =
    Boolean(slotId) && name.trim().length > 0 && /\S+@\S+\.\S+/.test(email);

  return (
    <Shell>
      <h1 className="text-xl font-bold text-gray-four">{sheet.title}</h1>
      {sheet.recordName && sheet.recordName !== sheet.title && (
        <div className="mt-1 text-sm text-gray-bold">{sheet.recordName}</div>
      )}
      {sheet.notes && (
        <p className="mt-3 whitespace-pre-wrap text-base text-gray-three">
          {sheet.notes}
        </p>
      )}
      <p className="mt-3 text-sm text-gray-bold">
        Times are shown in your local time zone ({localZone})
        {zone ? `; the session is offered in ${zone}` : ''}.
      </p>

      {sheet.slots.length === 0 ? (
        <div className="mt-6 rounded-xl bg-grey-50 px-4 py-4 text-base text-gray-three">
          Every offered time has been taken. Reply to the person who sent this
          link and they can offer more.
        </div>
      ) : (
        <form
          className="mt-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !book.isPending) book.mutate();
          }}
        >
          <div className="text-sm font-semibold text-gray-five">
            1. Pick a time
          </div>
          <div className="mt-2 space-y-4">
            {days.map(([day, slots]) => (
              <div key={day}>
                <div className="mb-1.5 text-sm font-medium text-gray-bold">
                  {day}
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {slots.map((s) => {
                    const on = s.id === slotId;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSlotId(s.id)}
                        className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                          on
                            ? 'border-blue-primary bg-blue-100 font-semibold text-blue-primary'
                            : 'border-white-two bg-white text-gray-five hover:border-blue-500'
                        }`}
                        aria-pressed={on}
                      >
                        <div>
                          {fmtTime(s.start)} – {fmtTime(s.end)}
                        </div>
                        {s.remaining > 1 && (
                          <div className="text-xs font-normal text-gray-bold">
                            {s.remaining} seats left
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-sm font-semibold text-gray-five">
            2. Your details
          </div>
          <div className="mt-2 grid gap-3">
            <div>
              <label className={label} htmlFor="bs-name">
                Name
              </label>
              <input
                id="bs-name"
                className={input}
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className={label} htmlFor="bs-email">
                Email
              </label>
              <input
                id="bs-email"
                type="email"
                className={input}
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className={label} htmlFor="bs-note">
                Anything we should know{' '}
                <span className="font-normal text-gray-bold">(optional)</span>
              </label>
              <textarea
                id="bs-note"
                className={input}
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          {problem && (
            <div className="mt-4 rounded-lg border border-red-500 bg-red-100 px-3.5 py-2.5 text-sm text-red-900">
              {problem}
            </div>
          )}

          <div className="mt-6">
            <button
              type="submit"
              className={primary}
              disabled={!canSubmit || book.isPending}
            >
              {book.isPending ? 'Booking…' : 'Confirm this time'}
            </button>
            {!slotId && (
              <div className="mt-2 text-xs text-gray-bold">
                Pick a time above first.
              </div>
            )}
          </div>
        </form>
      )}
    </Shell>
  );
};

export default BookSlot;
